<template>
  <v-form ref="ServicesForm" @submit="submitForm" v-focusNextOnEnter lazy-validation>
    <v-stepper-content :step="step" class="py-0 px-4">
      <v-row no-gutters>
        <v-col class="text-center col-3 col-md-3 col-sm-12 b-r-1">
          <service-checkbox
            :isSelectable="true"
            title="Honda Plus"
            :error="validVin"
            variant="honda-plus"
            :checkValue="services['honda-plus'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            data-cy="contracted-services-honda-plus"
            :forcedSelected="isForcedHondaPlus && !hasGoModel"
            :disabled="isForcedHondaMapit || isForcedDemoBike" />
          <div class="col-12 text-center py-6"></div>
          <service-checkbox
            v-if="hasGoModel"
            :isSelectable="true"
            title="Honda Plus Go"
            variant="honda-plus-go"
            :checkValue="services['honda-plus-go'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            data-cy="contracted-services-honda-plus-go"
            :disabled="isForcedHondaMapit || isForcedDemoBike || validVin" />
        </v-col>
        <v-col class="text-center col-3 col-md-3 col-sm-12 b-r-1">
          <service-checkbox
            :isSelectable="true"
            title="Honda Mapit"
            :error="mapitError"
            variant="honda-mapit"
            :checkValue="services['honda-mapit'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            data-cy="contracted-services-honda-mapit"
            :forcedSelected="isForcedHondaMapit || this.vehicle.forcedDemoBike" />
          <div class="px-8">
            <p class="label-field py-1 my-0 align-left">
              {{ $t(`honda.registration.services.honda-mapit.lbl-honda-mapit`) }}
            </p>
            <v-form v-model="validMapitCode">
              <available-device-lookup
                v-model="mapitCode"
                :ignore-check-rules="device => exDealerBike && exDealerBike.device.imei === device.imei"
                :required="services['honda-mapit'].selected"
                :disabled="!services['honda-mapit'].selected">
              </available-device-lookup>
            </v-form>
            <div
              v-if="services['honda-mapit'].selected && validMapitCode && exDealerBike != null"
              data-cy="ex-demo-bike-mapit-info">
              <div class="color-green" v-if="exDealerBike.device.imei === mapitCode.imei">
                {{ $t('honda.registration.services.honda-mapit.ex-demo-bike.mapit-info-text') }}
              </div>
              <div class="color-orange" v-else>
                {{ $t('honda.registration.services.honda-mapit.ex-demo-bike.mapit-warning-text') }}
              </div>
            </div>
          </div>
        </v-col>
        <v-col class="text-center col-3 col-md-3 col-sm-12 b-r-1">
          <service-checkbox
            :isSelectable="true"
            title="Honda Seguro"
            :error="''"
            variant="honda-seguro-normal"
            :checkValue="services['honda-seguro-normal'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            :disabled="!services['honda-plus'].selected && !services['honda-plus-go'].selected"
            data-cy="contracted-services-honda-insurance"
            :hideInfoButton="true" />
          <div class="col-12 text-center py-6"></div>
          <service-checkbox
            :isSelectable="true"
            title="Honda Seguro Ampliado"
            :error="''"
            variant="honda-seguro-plus"
            :checkValue="services['honda-seguro-plus'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            :disabled="!services['honda-plus'].selected && !services['honda-plus-go'].selected"
            data-cy="contracted-services-honda-extended-insurance"
            :hideInfoButton="true" />
        </v-col>
        <v-col class="text-center col-3 col-md-3 col-sm-12">
          <service-checkbox
            :isSelectable="true"
            title="Honda Financial Services"
            :error="''"
            variant="honda-finance"
            :checkValue="services['honda-finance'].selected"
            @changeCheckValue="changeCheckValue"
            @showInfo="showInfo"
            :disabled="
              (!services['honda-plus'].selected && !services['honda-plus-go'].selected) ||
              !availableFinancialServicesOption
            "
            data-cy="contracted-services-honda-financial-services"
            :hideInfoButton="true" />
        </v-col>
      </v-row>
    </v-stepper-content>
    <footer-actions :step="step">
      <template v-slot:left>
        <v-btn
          data-cy="registration-contracted-services-button-back"
          class="btn-gray"
          @click="backStep()"
          dark
          tabindex="-1"
          :disabled="isTransferVehicleRegistration">
          {{ $t('buttons.back') }}
        </v-btn>
      </template>
      <template v-slot:right>
        <v-btn
          data-cy="registration-contracted-services-button-continue"
          class="btn-primary"
          :key="buttonKey"
          @click.once="nextStep()"
          :disabled="!validSelectedServices">
          {{ $t('buttons.next') }}
        </v-btn>
      </template>
    </footer-actions>

    <mapit-dialog :dialog="dialog" @close="closeInfo" v-if="variantSelected">
      <template v-slot:header-text>
        <span>{{ $t(`honda.registration.services.${variantSelected}.title`) }}</span>
      </template>
      <template v-slot:body>
        <div class="mb-6" v-html="$t(`honda.registration.services.${variantSelected}.body`)" />
      </template>
    </mapit-dialog>
  </v-form>
</template>

<script>
import {Country} from '@mapit/common-utils/dist/types'
import {InsuranceType, Product} from '@muvio/honda-offers-builder/model'
import {mapGetters} from 'vuex'

import MapitDialog from '@/components/dialog/MapitDialog'
import AvailableDeviceLookup from '@/components/general/AvailableDeviceLookup'
import {RegistrationTypes} from '@/utils/constants'
import {servicesRegistrationModel} from '@/vuex/models'

import FooterActions from '../FooterActions'
import ServiceCheckbox from './ServiceCheckbox'

export default {
  name: 'Services',
  components: {AvailableDeviceLookup, ServiceCheckbox, FooterActions, MapitDialog},
  props: ['step', 'codeVinValidation'],
  data() {
    return {
      Country,
      services: servicesRegistrationModel(),
      mapitCode: null,
      dialog: false,
      variantSelected: null,
      validMapitCode: false,
      buttonKey: 1,
    }
  },
  watch: {
    isForcedDemoBike: function (val) {
      this.loadServiceStatus({forcedDemoBike: val})
    },
    getVinStatus: function (val) {
      this.loadServiceStatus({vinStatus: val})
    },
    hasGoModel: function () {
      if (this.isForcedHondaPlus) {
        this.services['honda-plus'].selected = true
        this.services['honda-plus-go'].selected = false
        this.services['honda-mapit'].selected = true
        this.services['honda-seguro-normal'].selected = false
        this.services['honda-seguro-plus'].selected = false
        this.services['honda-finance'].selected = false
      }
    },
    async mapitCode(val) {
      await this.$store.dispatch('addDevice', val)
    },
  },
  computed: {
    ...mapGetters({
      country: 'getCountry',
      offer: 'getOffer',
      hasGoModel: 'getHasGoModel',
      getVinStatus: 'getVinStatus',
      vehicle: 'getVehicle',
      exDealerBike: 'getExDealerBike',
      capabilities: 'getCapabilities',
      branch: 'getBranch',
    }),
    isCreatedFromOffer() {
      return this.$store.getters.getForcedType === RegistrationTypes.OFFER_REGISTRATION
    },
    isForcedDemoBike() {
      return this.vehicle.forcedDemoBike
    },
    mapitError() {
      return this.getVinStatus === 'REALTA_VIN_AND_MAPIT' && this.mapitCode.id
        ? this.$t('honda.registration.services.honda-mapit.vin.status_5')
        : ''
    },
    validVin() {
      return this.getVinStatus !== 'VIN_VALID'
        ? this.getVinStatus === 'ALREADY_USED_VIN'
          ? this.$t('honda.registration.services.honda-mapit.vin.status_1')
          : this.getVinStatus === 'REALTA_VIN' || this.getVinStatus === 'REALTA_VIN_AND_MAPIT'
            ? this.$t('honda.registration.services.honda-mapit.vin.status_2')
            : this.$t('honda.registration.services.honda-mapit.vin.status_3')
        : ''
    },
    validSelectedServices: function () {
      if (this.services['honda-mapit'].selected) {
        return (
          this.mapitCode &&
          this.validMapitCode &&
          !(this.services['honda-plus'].selected && this.services['honda-plus-go'].selected)
        )
      } else {
        return this.services['honda-plus'].selected ^ this.services['honda-plus-go'].selected
      }
    },
    isForcedHondaPlus() {
      return this.$store.getters.getForcedType === RegistrationTypes.HONDA_PLUS && !this.vehicle.forcedDemoBike
    },
    isForcedHondaMapit() {
      return (
        this.$store.getters.getForcedType === RegistrationTypes.HONDA_MAPIT ||
        this.$store.getters.getForcedType === RegistrationTypes.HONDA_MAPIT_TRANSFERRED
      )
    },
    isTransferVehicleRegistration() {
      return (
        this.$route.query.forcedType === RegistrationTypes.HONDA_MAPIT_TRANSFERRED &&
        this.$store.getters.getSelectedTransfer
      )
    },
    availableFinancialServicesOption() {
      return this.capabilities?.finance?.supported ?? this.$preferences(this.branch).finance.supported
    },
  },
  methods: {
    loadServiceStatus(data) {
      if (data.forcedDemoBike || this.isForcedDemoBike) {
        this.mapitCode = null
        this.services['honda-mapit'].selected = true
        this.services['honda-plus'].selected = false
        this.services['honda-plus-go'].selected = false
        this.services['honda-seguro-normal'].selected = false
        this.services['honda-seguro-plus'].selected = false
        this.services['honda-finance'].selected = false
      } else {
        //reset HondaMapit but always seslected
        if (!this.services['honda-mapit'].selected) {
          this.mapitCode = null
          this.services['honda-mapit'].selected = true
        }

        //HondaPlus
        if (this.isForcedHondaPlus) {
          this.services['honda-plus'].selected = (data.vinStatus || this.getVinStatus) === 'VIN_VALID'
          this.services['honda-plus-go'].selected = false

          if (!this.services['honda-plus'].selected) {
            this.services['honda-seguro-normal'].selected = false
            this.services['honda-seguro-plus'].selected = false
            this.services['honda-finance'].selected = false
          }
        }

        //ex-Demo Bike
        if (this.exDealerBike?.device) {
          this.mapitCode = this.exDealerBike.device
        }

        //Load services from offer
        if (this.isCreatedFromOffer) {
          const servicesProducts = this.offer.servicesProducts
          const servicesInsuranceType = this.offer.servicesInsuranceType
          this.services['honda-plus-go'].selected = false
          this.services['honda-plus'].selected =
            servicesProducts.includes(Product.HondaPlus) && (data.vinStatus || this.getVinStatus) === 'VIN_VALID'
          this.services['honda-mapit'].selected = servicesProducts.includes(Product.HondaMapit)
          if (this.services['honda-plus'].selected && servicesProducts.includes(Product.Insurance)) {
            if (
              servicesInsuranceType &&
              (servicesInsuranceType === InsuranceType.TodoRiesgo ||
                servicesInsuranceType === InsuranceType.RoboIncendio)
            ) {
              this.services['honda-seguro-normal'].selected = false
              this.services['honda-seguro-plus'].selected = true
            } else {
              this.services['honda-seguro-normal'].selected = true
              this.services['honda-seguro-plus'].selected = false
            }
          } else {
            this.services['honda-seguro-normal'].selected = false
            this.services['honda-seguro-plus'].selected = false
          }
          this.services['honda-finance'].selected =
            this.services['honda-plus'].selected && servicesProducts.includes(Product.Finance)
        }
      }
    },
    submitForm(e) {
      e.preventDefault()
    },
    backStep() {
      this.$emit('backStep')
    },
    async nextStep() {
      if (this.validSelectedServices) {
        if (this.services['honda-mapit'].selected && this.mapitCode.imei && this.validMapitCode) {
          this.services['honda-mapit'].value = this.mapitCode.imei
        }
        await this.$store.dispatch('addServices', this.services).then(() => {
          this.$emit('nextStep')
          this.buttonKey++
        })
      }
    },
    changeCheckValue(variant) {
      this.services[variant].selected = !this.services[variant].selected
      if (variant === 'honda-seguro-normal' && this.services[variant].selected) {
        this.services['honda-seguro-plus'].selected = false
      } else if (variant === 'honda-seguro-plus' && this.services[variant].selected) {
        this.services['honda-seguro-normal'].selected = false
      } else if (variant === 'honda-plus') {
        this.services['honda-plus-go'].selected = false
        this.services['honda-seguro-normal'].selected = false
        this.services['honda-seguro-plus'].selected = false
        this.services['honda-finance'].selected = false
      } else if (variant === 'honda-mapit') {
        if (this.services[variant].selected) {
          if (this.exDealerBike?.device) {
            this.mapitCode = this.exDealerBike.device
          }
        } else {
          this.mapitCode = null
        }
      } else if (variant === 'honda-plus-go') {
        if (this.services[variant].selected) {
          this.services['honda-mapit'].selected = true
          this.services['honda-plus'].selected = false
          if (!this.services['honda-seguro-plus'].selected) {
            this.services['honda-seguro-normal'].selected = true
            this.services['honda-seguro-plus'].selected = false
          }
          this.services['honda-finance'].selected = false
        } else {
          this.services['honda-seguro-normal'].selected = false
          this.services['honda-seguro-plus'].selected = false
          this.services['honda-finance'].selected = false
        }
      }
    },
    closeInfo() {
      this.infoServiceSelected = null
      this.dialog = false
    },
    showInfo(variant) {
      if (variant) {
        this.variantSelected = variant
        this.dialog = true
      } else {
        this.closeInfo()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.arrow-divider {
  width: 48px;
  height: 48px;
  background-color: var(--v-gray-base);
  border-radius: 100px;
  color: white;
  padding: 16px;
  font-size: 18px;
  margin-top: 5px;
  margin-bottom: 25px;
}
</style>
